/**
 * A query-builder "block" wires together the following query components:
 * - header (e.g. "USERS THAT <DID>")
 * - search-bar input (e.g. "Select event")
 * - dropdown (e.g. event selector, time picker)
 */

import {AttrsSchema, Component} from 'panel';
import findKey from 'lodash/findKey';
import pick from 'lodash/pick';

import {filterAttributes, serializeAttributes} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {emitTrackEvent, propagateEvent, rafRetry} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ButtonSize, ButtonTheme as ButtonThemes} from 'common/components/button/constants';
import {StackableComponent, Visibility} from 'common/components/base/stackable';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {
  eventPropertyDefinitionsStore,
  groupPropertyDefinitionsStore,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {Store} from 'common/report/stores/store';
import {currentProjectStore} from 'common/report/stores/project-details';
import {renderDateRangeTooltip} from 'common/widgets/date-range-preset-bar/date-range-tooltip';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext as DateRangeDisplayContext} from 'common/widgets/date-range-picker/models/types';
import {EntityTypeNameSingular} from 'common/report/util/saved-entities/types';
import {useStore} from 'common/util/component-helpers';
import {LabelTheme, SelectAllMechanism} from 'common/components/items-menu/types';
import {Directions} from 'common/components/drop-menu/types';
import {Placement, PositioningStrategy} from 'common/components/tooltip/types';

import {queryBuilderUtils} from '../query-builder/util';
import {ChildMenu} from './child-menu';
import {BehaviorType, MAX_LABEL_LENGTH, ModalType, QueryBlockTheme, SelectorType, TRACKING_EVENTS} from './constants';
import {BreakdownMenu} from './breakdown-menu';
import {CohortsMenu} from './cohorts-menu';
import {EventsMenu} from './events-menu';
import {FilterMenu} from './filter-menu';
import {MultiEventsMenu} from './multi-events-menu';
import {PropertiesMenu} from './properties-menu';
import {TimeMenu} from './time-menu';
/*eslint-disable-next-line import/no-cycle*/
import {CountMenu} from './count-menu';
import css from './stylesheets/index.inline.styl';
import {template} from './template';
import {updateEntityDescription} from './utils/util';
import {MenuType, QueryBlockAttrs, QueryBlockParams, QueryBlockState} from './types';
import {TypecastPillAction} from './properties-menu/types';

// Static definition of all registered child menus
export const MENU_TYPE_MAP: Record<MenuType, any> = {
  [MenuType.Breakdown]: BreakdownMenu,
  [MenuType.Cohorts]: CohortsMenu,
  [MenuType.Events]: EventsMenu,
  [MenuType.Filter]: FilterMenu,
  [MenuType.Properties]: PropertiesMenu,
  [MenuType.Tabbed]: ChildMenu, // TODO: deprecate this
  [MenuType.Time]: TimeMenu,
  [MenuType.MultiEvents]: MultiEventsMenu,
  [MenuType.Count]: CountMenu,
};

export const QueryBlockTagName = `mp-query-block`;

export enum Entity {
  Event = `event`,
  CustomEvent = `customEvent`,
  Cohort = `cohort`,
  UserProperty = `userProperty`,
  EventProperty = `eventProperty`,
  CustomProperty = `customProperty`,
}

export class QueryBlock extends Component<
  QueryBlockState,
  QueryBlockAttrs,
  unknown,
  unknown,
  unknown,
  QueryBlockParams
> {
  private inputElement: any;
  private contextPanelVisibilityTimerId: any;
  public _contextPanelParentElement: HTMLElement;
  // Needs to be public so it can be modified from tests
  public menuClosedTimestamp: number;

  static get attrsSchema(): AttrsSchema<QueryBlockAttrs> {
    return {
      ...super.attrsSchema,
      type: `string`,
      'all-data-groups': `boolean`,
      'allow-behavioral-property-creation': {
        type: `boolean`,
        description: `shows the 'Create behavioral property...' menu item in supported reports`,
      },
      'allow-typecast-pill-action': {
        type: `string`,
        description: `allows showing a pill labeled typecast next to eligible properties`,
        enum: Object.values(TypecastPillAction),
        default: null,
      },
      'apply-button-text': {type: `string`, description: `Only for properties menu type`},
      'behavior-type': {type: `string`, enum: Object.values(BehaviorType)},
      'highlight-selections': `boolean`,
      'min-selection': `number`,
      autofocus: `boolean`,
      'data-group-id': `string`,
      'data-groups': {
        type: `json`,
        default: [],
        description: `interface: DataGroup[]`,
      },
      'date-range': {type: `json`, description: `Only for time-comparison menu type`},
      disabled: `boolean`,
      'disabled-items': {
        type: `json`,
        default: {}, // dict for fast inclusion lookups
        description: `sets disabled options for the underlying mp-items-menu`,
      },
      'display-context': {type: `string`, default: null},
      'enable-enhanced-list-filters': `boolean`,
      'enable-workspace-filter-restrictions': `boolean`,
      'disable-value-selection': `boolean`,
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events`,
      },
      'exclude-custom-events': `boolean`,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'exclude-lookup-table-properties': {
        type: `boolean`,
        description: `whether to hide user uploaded properties from a lookup table`,
      },
      'excluded-elements': {type: `json`, default: []},
      'event-names': {type: `json`, default: null},
      'header-segments': `json`,
      'header-text': `string`,
      'help-text': `string`,
      'input-only': {
        type: `boolean`,
        description: `Set to ignore the selected value from rendering in the block label`,
      },
      tooltip: {
        type: `json`,
        description: `basic tooltip to show when a value is not selected.`,
      },
      'is-blocked-people': `boolean`,
      label: `string`,
      'max-data-history-days': {type: `number`, description: `max days time menu can query into data history`},
      multiple: `boolean`,
      'cohort-multiselect': `boolean`,
      open: `boolean`,
      'prefix-label': {type: `string`, default: null},
      'suffix-label': {type: `string`, default: null},
      'property-combine-mode': {type: `string`, default: null},
      'hide-events': `boolean`,
      'hide-all-properties-select': `boolean`,
      'project-has-people': `boolean`,
      'project-has-cohorts': `boolean`,
      'project-id': `number`,
      readonly: {type: `boolean`, description: `set block to read only mode `},
      removable: `boolean`,
      'dropdown-alignment': {type: `string`, default: `left`},
      selected: {type: `json`, default: null},
      inverted: `boolean`,
      'selector-icon': {type: `string`, default: null},
      'selector-right-icon': {type: `string`, default: null},
      'selector-type': {type: `string`, default: SelectorType.Text, enum: Object.values(SelectorType)},
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-all-tab': `boolean`,
      'show-all-users-cohort': `boolean`,
      'show-top-events': `boolean`,
      'single-data-group': `string`,
      'support-b2b-cohorts': `boolean`,
      'inline-cohorts-override': `json`,
      'is-multi-attribution': `boolean`,
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-special-date-property': `boolean`,
      'hide-special-date-property-unit': `boolean`,
      'show-special-dataset-property': `boolean`,
      tabs: `json`,
      theme: {
        type: `string`,
        default: ButtonThemes.Primary,
        enum: Object.values({...ButtonThemes, ...QueryBlockTheme}),
      },
      'button-size': {type: `string`, enum: Object.values(ButtonSize), default: ButtonSize.Medium},
      'track-name': {
        type: `string`,
        description: `If specified, includes "Report name" as a property`,
      },
      'other-group-by-options': {type: `json`, default: []},
      'other-filter-options': {type: `json`, default: []},
      'user-id': `number`,
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'search-placeholder': `string`,
      'show-select-all': `boolean`,
      'select-all-mechanism': {
        type: `string`,
        enum: Object.values(SelectAllMechanism),
        default: SelectAllMechanism.Full,
      },
      'select-all-disabled': `boolean`,
      'hide-recently-viewed': `boolean`,
      'hide-section-labels': `boolean`,
      'selectable-sensitive-properties': `boolean`,
      'menu-item-types': `json`,
      'menu-alignment': {type: `string`, enum: Object.values(Directions), default: Directions.Down},
      'menu-offset': {
        type: `number`,
        default: 0,
      },
      'advanced-menu-sections': {
        type: `json`,
        default: [],
      },
      'type-filter': {
        type: `json`,
        description: `only show properties with these types`,
        default: [],
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      slowThreshold: 155, // p75 from https://mixpanel.com/s/2kFNfR

      defaultState: {
        childMenu: Object.create(ChildMenu.prototype, {}),
        eventData: [],
        isHeaderMenuOpen: false,
        isMenuOpen: false,
        openedMenuForModal: false,
        isModalOpen: false,
        featureFlags: null,
        searchFilter: ``,
        selected: null,
        staged: null,
        tooltipData: null,
        keydownProxyEl: null,
        getSortedMenuSections: null,
      },
      params: {
        labelRenderer: Function,
        listProperty: Object,
      },
      helpers: {
        filterAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),
        MODAL_TYPE: ModalType,

        getContextPanelParentElement: (): Element => this._contextPanelParentElement,
        getSelectorTheme: (): QueryBlockTheme => {
          let theme = this.attr(`theme`);
          const isDefaultTheme = !this.hasAttribute(`theme`);
          const selectorType = this.attr(`selector-type`);

          const buttonTheme = findKey(ButtonThemes, (value) => value === theme);

          // validate button theme and use QueryBlockTheme.Secondary as a default
          if (selectorType === SelectorType.Button) {
            if (!buttonTheme) {
              throw Error(`Unkown button theme`);
            }

            theme = isDefaultTheme ? QueryBlockTheme.Secondary : theme;
          }

          if (this.attr(`readonly`) && theme !== QueryBlockTheme.QueryBuilder) {
            theme = QueryBlockTheme.Tertiary;
          }

          return theme;
        },

        getHeaderSegments: () => {
          if (this.attr(`header-text`)) {
            return [
              {
                type: `text`,
                value: this.attr(`header-text`),
              },
            ];
          }
          return this.attr(`header-segments`) || [];
        },

        handleCancel: () => {
          this.closeMenu();
        },

        handleChildMenuClosedModal: () => {
          this.update({isModalOpen: false});
          if (this.state.openedMenuForModal) {
            this.closeMenu();
          }
        },

        handleChildMenuOpenedModal: () => {
          this.update({isModalOpen: true});
        },

        // Handle actions from the context tooltip attached to the query block. First, give the
        // child menu a chance to handle the action. If it's not handled by child menu, then
        // handle the action directly here.
        handleTooltipContextAction: (ev) => {
          const {handled, openedModal} = this.childMenu.handleTooltipContextAction(ev) || {
            handled: false,
            openedModal: false,
          };
          if (!handled) {
            throw Error(`unhandled context panel action`);
          }
          this.update({
            tooltipData: null,
            // If a modal that lives in the child menu is open, then the child menu has to be open.
            // This can happen if you click the edit action on the context panel attached to the query block.
            // This will open the child menu's modal. However, we also have to force open the child menu,
            // otherwise the modal would be hidden.
            ...(openedModal ? {isMenuOpen: true, openedMenuForModal: true, isModalOpen: true} : {}),
          });
        },

        handlePropagatedtooltipContextEditDescription: (ev) => {
          const detail = ev.detail;
          switch (detail.item.type) {
            case EntityTypeNameSingular.Cohort:
              this.updateCohortDescription(detail);
              break;
            case EntityTypeNameSingular.EventProperty:
              this.updatePropertyDescription(detail);
              break;
            case EntityTypeNameSingular.UserProperty:
              this.updatePropertyDescription(detail);
              break;
            case EntityTypeNameSingular.CustomProperty:
              this.updateCustomPropertyDescription(detail);
              break;
            default:
              break;
          }
        },

        handleSampleEvent: (ev) => {
          this.track(TRACKING_EVENTS.SAMPLE_EVENT, {}, false);

          window.open(ev.detail.item.url, `_blank`);
        },

        getHeaderMenuSection: (headerSegment) => [
          {
            // format for mp-items-menu
            items: headerSegment.items.map((item) => ({
              ...item,
              isSelected: item.value === headerSegment.selectedItem.value,
              labelTheme: LabelTheme.Strong,
            })),
          },
        ],

        getTheme: () => this.attr(`theme`),
        isReady: () => !!this.attr(`project-id`) || [`time`, `time-comparison`].includes(this.attr(`type`)),
        isReadOnly: () => this.attr(`readonly`) || this.childMenu.canOpenMenu() === false,
        showInput: () => !this.attr(`input-only`),
        menuHasCustomWidth: (): boolean => {
          // note: only relevant for the time menu at the moment
          return this.attr(`type`) === `time`;
        },

        // label config overridden by child menu
        getPrefixLabel: () =>
          this.attr(`prefix-label`) === null ? this.childMenu.getPrefixLabel() : this.attr(`prefix-label`),
        selectedLabel: () =>
          truncateMiddle(this.childMenu.selectedLabel(), MAX_LABEL_LENGTH) || this.state.selected?.label,
        selectedBrowserTooltipLabel: () => this.childMenu.selectedBrowserTooltipLabel(),
        selectedLeftIcon: () => this.childMenu.selectedLeftIcon(),
        selectedRightIcon: () => this.childMenu.selectedRightIcon(),
        getSelections: () => this.childMenu.getSelections(),
        isUnsavedCustomEntity: () => this.childMenu.isUnsavedCustomEntity(),

        labelInputInserted: (vnode) => this.update({keydownProxyEl: vnode.elm}),

        handleHeaderSelectClicked: () => {
          this.update({isHeaderMenuOpen: !this.state.isHeaderMenuOpen});
        },

        handleHeaderMenuVisibilityChange: (ev) => {
          if (ev.detail.state !== `open`) {
            this.update({isHeaderMenuOpen: false});
          }
        },

        handleHeaderSelectionChanged: (ev) => {
          this.update({isHeaderMenuOpen: false});
          this.dispatchEvent(new CustomEvent(`select-header-item`, {detail: ev.detail.item}));
        },

        //#region Custom properties
        handleDelete: (ev) => this.helpers.handleRemove(ev),
        //#endregion

        handleSearchInput: (ev) => this.update({searchFilter: ev.target.value}),
        handleSelect: (ev) => this.dispatchSelect(ev.detail),
        handleLabelClick: () => {
          return this.attr(`disabled`) || this.attr(`readonly`) || this.openMenu();
        },
        handleLabelMouseEnter: () => {
          // This is a a heuristic to prevent the following behavior: The user click the mp-query-block button and then
          // selects an item with just keyboard shortcuts. When the mp-items-menu is closed (because the mouse is still
          // on top of the mp-query-block button) this mouseenter event handler will retrigger causing
          // the context panel to be shown. I can't think of a better way to tell detect the scenario above, so using
          // the timestamp of closing menu as a heuristic to prevent reshowing the context panel within that timeframe.
          const menuWasRecentlyClosed =
            Number.isFinite(this.menuClosedTimestamp) && Date.now() - this.menuClosedTimestamp < 250;
          const anotherMenuIsOpen =
            StackableComponent.getStackingContextDepthForElement(this) + 1 <=
            StackableComponent.getCurrentStackingContextDepth();
          if (!this.state.isMenuOpen && !menuWasRecentlyClosed && !anotherMenuIsOpen) {
            this.delayShowContextPanel();
          }
          this.childMenu.handleLabelMouseEnter();
        },
        handleLabelMouseLeave: () => {
          this.delayHideContextPanel();
          this.childMenu.handleLabelMouseLeave();
        },
        handleContextPanelHoverIn: () => {
          this.dispatchEvent(new CustomEvent(`mouseEnterContextMenu`, {bubbles: true}));
          this.clearContextPanelVisibilityTimer();
        },
        handleContextPanelHoverOut: () => {
          this.dispatchEvent(new CustomEvent(`mouseLeaveContextMenu`, {bubbles: true}));
          this.delayHideContextPanel();
        },
        handleMenuChange: (ev) => {
          const {state} = ev.detail;
          if (state === Visibility.Closed) {
            this.closeMenu();
          } else if (this.inputElement && this.attr(`type`) !== `time`) {
            // This patch is equivalent fo throwing a sleep retry since
            // However, it is a goot fix while @pablo works on a real solution
            rafRetry(() => this.inputElement.focus());
          }

          if (state === Visibility.Open) {
            this.clearContextPanelVisibilityTimer();
            this.dispatchEvent(new CustomEvent(`open`));
          }
        },

        handleMouseDown: () => this.clearContextPanelVisibilityTimer(),

        handleRemove: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`remove`));
        },

        handleSelectAll: ({detail}) => {
          this.dispatchEvent(new CustomEvent(`selectAll`, {detail}));
        },

        menuInserted: (vnode) => {
          requestAnimationFrame(() => {
            this.update({childMenu: vnode.elm as ChildMenu<any>});
            this.updateSelection();
          });
        },

        searchInputInserted: (vnode) => {
          this.inputElement = vnode.elm;
          this.update({keydownProxyEl: this.inputElement});
        },

        /**
         * Returns all attributes mirrored from this component to the
         * underlying selection menu in a serialized form
         */
        getChildMenuAttributes: () => {
          const [, menuClass] = this.childMenuComponentEntry;
          const attributes = {
            ...pick(this.attrs(), Object.keys(menuClass.attrsSchema)),
            open: this.state.isMenuOpen && !this.state.openedMenuForModal,
            'search-filter': this.state.searchFilter,
            elRef: `childMenu`,
          };

          // TODO: I'm not sure if this is the best place to do this but this is to fix a bug caused
          // by removing the segment-menu
          if (`other-options` in menuClass.attrsSchema && this.attr(`other-group-by-options`)?.length) {
            attributes[`other-options`] = this.attr(`other-group-by-options`);
          } else if (`other-options` in menuClass.attrsSchema && this.attr(`other-filter-options`)?.length) {
            attributes[`other-options`] = this.attr(`other-filter-options`);
          }

          return serializeAttributes(attributes);
        },

        getChildMenuParams: () => {
          const {listProperty} = this.params;
          return listProperty ? {listProperty} : {};
        },

        getContextPanelAttrs: () => ({
          'position-strategy': PositioningStrategy.Fixed,
          placement: queryBuilderUtils.isCoreReport() ? Directions.Right : Directions.Down,
          offset: queryBuilderUtils.isCoreReport() ? 8 : 4,
          'show-arrow': true,
          elRef: `contextPanel`,
        }),

        /**
         * A given resource context panel (event, cohort or properties) needs to be defined
         * in the context of that menu type, to prevent having resource specific logic
         * in this main component
         */
        renderContextPanelFromMenu: () => {
          const contextPanelRenderer = MENU_TYPE_MAP[this.attr(`type`)].contextPanelRenderer;

          return (
            contextPanelRenderer &&
            contextPanelRenderer({
              tooltipData: this.state.tooltipData,
              contextPanelAttrs: this.helpers.getContextPanelAttrs(),
              handleSelect: this.dispatchSelect.bind(this),
              handleDelete: () => this.dispatchEvent(new CustomEvent(`remove`)),
              track: this.track.bind(this),
              handleHoverIn: this.helpers.handleContextPanelHoverIn.bind(this),
              handleHoverOut: this.helpers.handleContextPanelHoverOut.bind(this),
              additionalArgs: this.helpers.getChildMenuAttributes(),
              contextPanelProps: queryBuilderUtils.isCoreReport()
                ? {tooltipPositionParentElement: this._contextPanelParentElement}
                : {},
            })
          );
        },

        renderSelectedDateTooltipIfNeeded: () => {
          const isTimeSelector = this.attr(`type`) === `time`;
          if (isTimeSelector && this.state.selected) {
            const dateRangeClass = DATE_RANGE_TYPES[this.attr(`display-context`) || DateRangeDisplayContext.Default];
            return renderDateRangeTooltip(
              new dateRangeClass(this.state.selected),
              currentProjectStore,
              queryBuilderUtils.isCoreReport() ? Placement.Bottom : Placement.Top,
            );
          }
          return null;
        },
        onAutofocusableElAdded: (elm) => {
          if (this.attr(`autofocus`) && !this.state.isMenuOpen) {
            requestAnimationFrame(() => elm.focus());
          }
        },

        /** helpers are this bound, a little helper function that simply re-draws the UI when any store values change */
        updateAfterStoreValueChange: () => this.update(),
      },
    };
  }

  get childMenu() {
    return this.state.childMenu;
  }

  get childMenuComponentName() {
    const [type] = this.childMenuComponentEntry;
    return `mp-${type}-menu`;
  }

  // attempts to find a multi selection component
  // first and defaults to type-menu
  get childMenuComponentEntry() {
    let type = this.attr(`type`);
    if (this.attr(`multiple`) && type === MenuType.Events) {
      type = MenuType.MultiEvents;
    }

    return [type, MENU_TYPE_MAP[type]];
  }

  openMenu() {
    if (!this.state.isMenuOpen) {
      this.update({isMenuOpen: true, tooltipData: null});
      this.childMenu.handleLabelClick();
    }
  }

  closeMenu() {
    this.update({
      isMenuOpen: false,
      openedMenuForModal: false,
      searchFilter: ``,
      tooltipData: null,
      staged: null,
    });
    this.menuClosedTimestamp = Date.now();
    this.childMenu.handleCloseMenu();
    this.dispatchEvent(new CustomEvent(`close`));
  }

  connectedCallback() {
    super.connectedCallback();

    useStore(this, currentProjectStore).fetch();
  }

  disconnectedCallback() {
    this.clearContextPanelVisibilityTimer();
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    switch (attr) {
      case `open`:
        this.update({isMenuOpen: this.attr(`open`)});
        break;
      case `selected`:
        this.updateSelection();
        break;
      default:
        break;
    }
  }

  updateSelection() {
    this.update({selected: this.childMenu.transformSelectedVal(this.attr(`selected`))});
  }

  dispatchSelect(detail) {
    this.dispatchEvent(new CustomEvent(`select`, {detail}));

    if (detail.commit ?? this.childMenu.shouldCloseOnSelect(detail)) {
      this.closeMenu();
    }
  }

  track(event, props, cancelable = true) {
    if (!this.attr(`track-name`)) {
      return;
    }

    emitTrackEvent(this, {
      eventName: event,
      props: {
        ...props,
        project_id: this.attr(`project-id`),
        tab: this.attr(`track-name`),
      },
      cancelable,
    });
  }

  clearContextPanelVisibilityTimer() {
    if (this.contextPanelVisibilityTimerId) {
      clearTimeout(this.contextPanelVisibilityTimerId);
    }
    this.contextPanelVisibilityTimerId = null;
  }

  delayShowContextPanel() {
    this.clearContextPanelVisibilityTimer();
    this.contextPanelVisibilityTimerId = setTimeout(() => {
      if (this.state.isMenuOpen) {
        return;
      }

      const tooltipData = this.childMenu.getSelectedHoverTooltipData();
      this.dispatchEvent(new CustomEvent(`openContextMenu`, {detail: {isOpen: true}, bubbles: true}));
      this.update({tooltipData});
      if (tooltipData) {
        // Setting cancelable to false is needed to prevent mp-query-multi-block from swallowing the event
        const cancelable = false;
        this.track(
          TRACKING_EVENTS.HOVER_CONTEXT_PANEL,
          {
            'Entity name': tooltipData.header,
            'Query block type': this.attr(`type`),
            'Has description': !!tooltipData.description,
            ...(this.childMenu.getSelectedTrackingProps() || {}),
          },
          cancelable,
        );
      }
    }, 375);
  }

  /** @todo [pablo] move menu specific context panel actions to menu-type/context-panel.tsx */
  async updateEntityDescription(
    entityStore: Store<any>,
    newDescription,
    storeKey,
    updateRequestObj,
    undoRequestObj,
    entityType,
  ) {
    return updateEntityDescription(
      entityStore,
      newDescription,
      storeKey,
      updateRequestObj,
      undoRequestObj,
      entityType,
      this.track.bind(this),
    );
  }

  updateCohortDescription(detail) {
    const cohortStoreKey = detail.item.id;
    const descriptionBeforeUpdate = (cohortsStore.value[cohortStoreKey] as any).description;
    const cohortPropertyUpdate = {description: detail.description};

    const updateRequestObj = {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
      cohortID: detail.item.id,
      cohort: cohortPropertyUpdate,
    };
    const undoRequestObj = {...updateRequestObj, cohort: {description: descriptionBeforeUpdate}};
    this.updateEntityDescription(
      cohortsStore,
      detail.description,
      cohortStoreKey,
      updateRequestObj,
      undoRequestObj,
      `cohort property`,
    );
  }

  updatePropertyDescription(detail) {
    const propertyStoreKey = detail.item.name;
    let propertyStore;

    if (detail.item.dataGroupId) {
      propertyStore = groupPropertyDefinitionsStore;
    } else {
      propertyStore =
        detail.item.type === EntityTypeNameSingular.EventProperty
          ? eventPropertyDefinitionsStore
          : peoplePropertyDefinitionsStore;
    }

    const descriptionBeforeUpdate = propertyStore.value[propertyStoreKey].description;

    let propertyDefinition;

    if (detail.item.id) {
      propertyDefinition = {id: detail.item.id, description: detail.description, dataGroupId: detail.item.dataGroupId};
    } else {
      propertyDefinition = {
        name: detail.item.name,
        resourceType: detail.item.type === EntityTypeNameSingular.EventProperty ? `Event` : `User`,
        description: detail.description,
        dataGroupId: detail.item.dataGroupId,
      };
    }

    const updateRequestObj = {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
      propertyDefinition,
    };

    const undoRequestObj = {
      ...updateRequestObj,
      propertyDefinition: {...updateRequestObj.propertyDefinition, description: descriptionBeforeUpdate},
    };

    this.updateEntityDescription(
      propertyStore,
      detail.description,
      propertyStoreKey,
      updateRequestObj,
      undoRequestObj,
      detail.item.type === EntityTypeNameSingular.EventProperty ? `event property` : `user property`,
    );
  }

  async updateCustomPropertyDescription(detail) {
    const customPropertyId = detail.item.id;

    const customProperty = Object.assign(
      {},
      {...customPropertiesStore.value[customPropertyId], description: detail.description},
    );
    const oldDescription = customPropertiesStore.value[customPropertyId].description;
    const cancelable = false;
    this.track(
      TRACKING_EVENTS.EDIT_DESCRIPTION,
      {
        'New Description': detail.description,
        'old description': oldDescription,
        'access point': `context panel`,
        'Entity Type': `custom property`,
      },
      cancelable,
    );
    try {
      await customPropertiesStore.put(customPropertyId, {
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
        customPropertyId,
        customProperty,
      });
      const undoCallback = async () => {
        await customPropertiesStore.put(customPropertyId, {
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
          customPropertyId,
          customProperty: {...customProperty, description: oldDescription},
        });
      };
      addToast({
        message: `'${detail.item.name}' has been updated`,
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback,
      });
    } catch (err) {
      addToast({message: `Failed to update the description`, type: ToastType.ERROR});
    }
  }

  // Delay closing the context panel so that when moving the mouse from the
  // query block to the context panel, the context doesn't get closed.
  delayHideContextPanel() {
    this.clearContextPanelVisibilityTimer();
    this.contextPanelVisibilityTimerId = setTimeout(() => {
      if (this.state.isMenuOpen) {
        return;
      }
      this.update({tooltipData: null});
      this.dispatchEvent(new CustomEvent(`closeContextMenu`, {detail: {isOpen: false}, bubbles: true}));
    }, 100);
  }

  set getSortedMenuSections(getSortedMenuSections) {
    this.update({getSortedMenuSections});
  }

  get contextPanelParentElement() {
    return this._contextPanelParentElement;
  }

  set contextPanelParentElement(el) {
    this._contextPanelParentElement = el;
  }
}

export const QueryBlockEl = defineAndMakeJsxFactory(QueryBlockTagName, QueryBlock);
export const QueryBlockReact = panel2react(QueryBlockTagName, QueryBlock);
